import {
  ApiErrorSchema,
  AdminOverviewResponseSchema,
  AdminReviewListResponseSchema,
  AdminTherapistListResponseSchema,
  AdminUserListResponseSchema,
  ModerateReviewResponseSchema,
  UpdateTherapistProfileResponseSchema,
  UpdateTherapistStatusResponseSchema,
  UpdateUserStatusResponseSchema,
  type AdminOverviewResponse,
  type AdminReviewListResponse,
  type AdminTherapistListResponse,
  type AdminUserListQuery,
  type AdminUserListResponse,
  type ModerateReviewResponse,
  type TherapistStatus,
  type UpdateTherapistProfileRequest,
  type UpdateTherapistProfileResponse,
  type UpdateTherapistStatusResponse,
  type UpdateUserStatusResponse,
} from '@ah/contracts';

/** Thin fetch wrapper for the admin endpoints — same shape as the other apps' *.api.ts files. */
export class AdminApi {
  constructor(private readonly baseUrl: string) {}

  async getOverview(accessToken: string): Promise<AdminOverviewResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/admin/overview`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return AdminOverviewResponseSchema.parse(await res.json());
  }

  async listTherapists(accessToken: string, status?: TherapistStatus): Promise<AdminTherapistListResponse> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`${this.baseUrl}/api/v1/admin/therapists${qs}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return AdminTherapistListResponseSchema.parse(await res.json());
  }

  async setTherapistStatus(
    accessToken: string,
    therapistProfileId: string,
    status: TherapistStatus,
    reason?: string,
  ): Promise<UpdateTherapistStatusResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/admin/therapists/${therapistProfileId}/status`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ status, reason }),
    });
    if (!res.ok) throw await this.toError(res);
    return UpdateTherapistStatusResponseSchema.parse(await res.json());
  }

  async listUsers(accessToken: string, query: Partial<AdminUserListQuery> = {}): Promise<AdminUserListResponse> {
    const params = new URLSearchParams();
    if (query.role) params.set('role', query.role);
    if (query.q) params.set('q', query.q);
    if (query.suspended !== undefined) params.set('suspended', String(query.suspended));
    const qs = params.toString();
    const res = await fetch(`${this.baseUrl}/api/v1/admin/users${qs ? `?${qs}` : ''}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return AdminUserListResponseSchema.parse(await res.json());
  }

  async setUserStatus(
    accessToken: string,
    userId: string,
    suspended: boolean,
    reason?: string,
  ): Promise<UpdateUserStatusResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/admin/users/${userId}/status`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ suspended, reason }),
    });
    if (!res.ok) throw await this.toError(res);
    return UpdateUserStatusResponseSchema.parse(await res.json());
  }

  async listReviews(accessToken: string, hidden?: boolean): Promise<AdminReviewListResponse> {
    const qs = hidden !== undefined ? `?hidden=${hidden}` : '';
    const res = await fetch(`${this.baseUrl}/api/v1/admin/reviews${qs}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return AdminReviewListResponseSchema.parse(await res.json());
  }

  async moderateReview(
    accessToken: string,
    reviewId: string,
    hidden: boolean,
    reason?: string,
  ): Promise<ModerateReviewResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/admin/reviews/${reviewId}/moderate`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ hidden, reason }),
    });
    if (!res.ok) throw await this.toError(res);
    return ModerateReviewResponseSchema.parse(await res.json());
  }

  async updateTherapistProfile(
    accessToken: string,
    therapistProfileId: string,
    input: UpdateTherapistProfileRequest,
  ): Promise<UpdateTherapistProfileResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/admin/therapists/${therapistProfileId}/profile`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw await this.toError(res);
    return UpdateTherapistProfileResponseSchema.parse(await res.json());
  }

  private async toError(res: Response): Promise<Error> {
    try {
      const payload = ApiErrorSchema.parse(await res.json());
      return new AdminApiError(payload.error.code, payload.error.message, res.status);
    } catch {
      return new AdminApiError('INTERNAL_ERROR', res.statusText, res.status);
    }
  }
}

export class AdminApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}
